import {
  GiAnkh,
  GiAnvil,
  GiBattleAxe,
  GiBowArrow,
  GiBurningEmbers,
  GiCastle,
  GiCoins,
  GiCrossbow,
  GiCrossedSwords,
  GiCrystalBars,
  GiCrystalize,
  GiFireSilhouette,
  GiFountain,
  GiHealthPotion,
  GiHammerNails,
  GiKnapsack,
  GiKnightBanner,
  GiLightningTrio,
  GiMineWagon,
  GiPerson,
  GiPodiumWinner,
  GiSpartanHelmet,
  GiSpellBook,
  GiStoneBlock,
  GiTombstone,
  GiTreasureMap,
  GiWizardStaff,
  GiWoodAxe,
  GiWoodPile,
  GiWoodenCrate,
  GiBootStomp,
  GiCalendarHalfYear,
  GiScrollUnfurled,
} from "react-icons/gi";
import { MdGroups, MdForum } from "react-icons/md";

import type { IconType } from "react-icons";
import { BuildingType } from "../types/castle";

// Building type → game-icons.net icon.
// Matches BuildingType enum in shared/Buildings/BuildingType.cs.
// SignatureForge (9) varies by player element — see SignatureBuildingIcons.
export const BuildingIcons: Record<number, IconType> = {
  0: GiCastle,                // Castle
  1: GiMineWagon,             // GoldMine
  2: GiWoodAxe,               // Lumberjack
  3: GiStoneBlock,            // Quarry
  4: GiCrystalize,            // ManaAltar
  5: GiWoodenCrate,           // Warehouse
  6: GiSpartanHelmet,         // Barracks
  7: GiCrossbow,              // ArcheryRange
  8: GiWizardStaff,           // MageTower
  9: GiAnvil,                 // SignatureForge (fallback; overridden per element)
  10: GiHealthPotion,         // Hospital
  11: GiBattleAxe,            // WarHall (march slots)
  12: GiSpellBook,            // Academy
  13: GiKnightBanner,         // HeroHall
  14: GiHammerNails,          // BuilderHut
  15: GiAnkh,                 // TempleOfResurrection
  16: GiStoneBlock,           // Wall — крепостная стена
  17: GiCrossbow,             // ArrowTower — лучная башня
  18: GiBootStomp,            // TrapField — ловушки
};

// Signature building per element — themed icons that match the per-element
// building names ("Пепельный алтарь" / "Священное озеро" / "Чертог бурь" /
// "Каменный курган").
export const SignatureBuildingIcons: Record<number, IconType> = {
  0: GiBurningEmbers,         // Fire — burning embers, like an ashen altar
  1: GiFountain,              // Water — sacred lake / spring
  2: GiLightningTrio,         // Air — storm hall
  3: GiTombstone,             // Earth — burial mound / cairn
};

// Unit class → icon (Infantry/Ranger/Mage/Signature). Signature is
// overridden per unit-id via SignatureUnitIcons.
export const UnitClassIcons: Record<number, IconType> = {
  0: GiSpartanHelmet,
  1: GiBowArrow,
  2: GiWizardStaff,
  3: GiFireSilhouette,        // Generic fallback when unit id unknown
};

// Signature units by unit-id. Each element's signature creature gets an
// element-themed icon. Used as a react-icons fallback when the unit PNG
// is not available (or as a small inline icon in event logs etc).
export const SignatureUnitIcons: Record<string, IconType> = {
  "fire-phoenix": GiBurningEmbers,
  "water-mermaid": GiFountain,
  "air-griffin": GiLightningTrio,
  "earth-titan": GiTombstone,
};

// PNG icon paths — painterly portraits/scenes from DALL-E collages.
// Buildings (16) and units (8) live in /webapp/public/icons/.
export const BuildingPngByType: Record<number, string> = {
  0: "/icons/buildings/castle.png",
  1: "/icons/buildings/gold-mine.png",
  2: "/icons/buildings/lumberjack.png",
  3: "/icons/buildings/quarry.png",
  4: "/icons/buildings/mana-altar.png",
  5: "/icons/buildings/warehouse.png",
  6: "/icons/buildings/barracks.png",
  7: "/icons/buildings/archery-range.png",
  8: "/icons/buildings/mage-tower.png",
  // 9 = SignatureForge — element-specific, see SignatureBuildingPngByElement
  10: "/icons/buildings/hospital.png",
  11: "/icons/buildings/war-hall.png",
  12: "/icons/buildings/academy.png",
  13: "/icons/buildings/hero-hall.png",
  14: "/icons/buildings/builder-hut.png",
  15: "/icons/buildings/temple.png",
};

// Element-specific PNGs for the Signature building, all four painterly
// scenes from DALL-E:
//   Fire  — Пепельный алтарь (black altar with embers and rising flame)
//   Water — Священное озеро (circular lagoon, stone pillars, lotus flowers)
//   Air   — Чертог бурь (open pillared temple with purple lightning)
//   Earth — Каменный курган (megalithic moss-covered stone cairn)
export const SignatureBuildingPngByElement: Record<number, string> = {
  0: "/icons/buildings/signature-fire.png",
  1: "/icons/buildings/signature-water.png",
  2: "/icons/buildings/signature-air.png",
  3: "/icons/buildings/signature-earth.png",
};

// Bottom-nav icons.
export const NavIcons = {
  castle: GiCastle,
  army: GiCrossedSwords,
  inventory: GiKnapsack,
  me: GiPerson,
  marches: GiBootStomp,
  alliance: MdGroups,
  chat: MdForum,
  quests: GiScrollUnfurled,
} as const;

interface BuildingIconProps {
  type: number;
  playerElement?: number;
  size?: number;
  className?: string;
}

// Renders the best available icon for a building: PNG if we have one,
// otherwise react-icons (with element-aware fallback for the signature
// building).
export function BuildingPaintedIcon({
  type,
  playerElement,
  size = 28,
  className = "",
}: BuildingIconProps) {
  let pngPath: string | undefined;
  if (type === BuildingType.SignatureForge && playerElement != null) {
    pngPath = SignatureBuildingPngByElement[playerElement];
  }
  if (!pngPath) {
    pngPath = BuildingPngByType[type];
  }
  if (pngPath) {
    return (
      <img
        src={pngPath}
        width={size}
        height={size}
        alt=""
        className={`h-full w-full rounded-md object-cover ${className}`.trim()}
        loading="lazy"
      />
    );
  }
  // Fallback: react-icons. Signature → element-specific, else generic.
  let Icon: IconType | undefined;
  if (type === BuildingType.SignatureForge && playerElement != null) {
    Icon = SignatureBuildingIcons[playerElement];
  }
  Icon = Icon ?? BuildingIcons[type] ?? GiHammerNails;
  return <Icon size={size} className={className} />;
}

// Misc shared icons used across screens.
export const BattleIcons = {
  attack: GiCrossedSwords,
  treasure: GiTreasureMap,
  gold: GiCoins,
  wood: GiWoodPile,
  stone: GiStoneBlock,
  mana: GiCrystalBars,
  trophy: GiPodiumWinner,
  event: GiCalendarHalfYear,
} as const;
